import type { Enums } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'expo-router'
import { useState } from 'react'
import { Pressable, View } from 'react-native'
import { useDebouncedCallback } from 'use-debounce'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { Body, Label } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { trpc } from '~/lib/trpc'

type UserRole = Enums<'user_role'>

function getRoleLabel(role: UserRole, t: ReturnType<typeof useTranslations<'admin'>>['t']): string {
  const roleMap: Record<UserRole, string> = {
    realtor: t(($) => $.users.roles.realtor),
    buyer: t(($) => $.users.roles.buyer),
    seller: t(($) => $.users.roles.seller),
  }
  return roleMap[role]
}

type AdminUser = {
  userId: string
  name: string
  email: string | null | undefined
  phoneNumber: string | null | undefined
  role: UserRole
  slug: string | null | undefined
}

function UserRow({ user }: { user: AdminUser }) {
  const { t } = useTranslations('admin')

  return (
    <Link href={`/(authenticated)/admin/users/${user.userId}`} asChild>
      <Pressable className="flex-row items-center justify-between gap-4 rounded-lg border border-neutral-200 bg-white p-4 active:bg-neutral-50">
        <View className="flex-1 gap-1">
          <Text className="mb-1 font-medium text-base">{user.name}</Text>

          {!!user.phoneNumber && (
            <Label className="text-sm text-secondary">{user.phoneNumber}</Label>
          )}

          {!!user.email && <Label className="text-sm text-secondary">{user.email}</Label>}

          {!!user.slug && <Label className="text-xs text-neutral-400">{user.slug}</Label>}
        </View>

        <View className="rounded-md bg-neutral-100 px-3 py-1.5">
          <Text className="text-sm text-neutral-600">{getRoleLabel(user.role, t)}</Text>
        </View>
      </Pressable>
    </Link>
  )
}

const PAGE_SIZE = 20

export default function Users() {
  const { t } = useTranslations('admin')
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    setSearch(value)
    setPage(1)
  }, 300)

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSetSearch(value)
  }

  const queryInput = { page, limit: PAGE_SIZE, search: search || undefined }

  const { data, isLoading, error, isFetching } = useQuery(
    trpc.admin.listUsersForAdmin.queryOptions(queryInput)
  )

  const users = data?.users ?? []
  const totalPages = data?.totalPages ?? 1
  const totalCount = data?.totalCount ?? 0
  const currentPage = data?.page ?? page

  const canGoBack = currentPage > 1
  const canGoNext = currentPage < totalPages

  return (
    <ScreenView>
      <ContentBox className="flex-1" contentContainerClassName="gap-6" variant="transparent">
        <View className="gap-4">
          <Heading2 className="text-center">{t(($) => $.users.title)}</Heading2>
          <Body className="text-center text-primary">{t(($) => $.users.description)}</Body>
        </View>

        <Link href="/(authenticated)/admin/users/add" asChild>
          <Button variant="outline">
            <ButtonLabel>{t(($) => $.createUser.button)}</ButtonLabel>
          </Button>
        </Link>

        <Input
          value={searchInput}
          onChangeText={handleSearchChange}
          placeholder={t(($) => $.users.searchPlaceholder)}
          variant="box"
          className="bg-white"
        />

        {isLoading && (
          <Body className="text-center text-neutral-500">{t(($) => $.users.loading)}</Body>
        )}

        {!!error && <Body className="text-center text-red-600">{t(($) => $.users.loadError)}</Body>}

        {!isLoading && !error && users.length === 0 && (
          <Body className="text-center text-neutral-500">
            {search ? t(($) => $.users.noResults) : t(($) => $.users.none)}
          </Body>
        )}

        {users.length > 0 && (
          <>
            <View className="gap-3">
              {users.map((user) => (
                <UserRow key={user.userId} user={user} />
              ))}
            </View>

            <View className="flex-row items-center justify-between gap-4 pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!canGoBack || isFetching}
                onPress={() => setPage(currentPage - 1)}>
                <ButtonLabel>{t(($) => $.users.previous)}</ButtonLabel>
              </Button>

              <Text className="text-sm text-neutral-600">
                {t(($) => $.users.page, {
                  current: currentPage,
                  total: totalPages,
                  count: totalCount,
                })}
              </Text>

              <Button
                variant="outline"
                size="sm"
                disabled={!canGoNext || isFetching}
                onPress={() => setPage(currentPage + 1)}>
                <ButtonLabel>{t(($) => $.users.next)}</ButtonLabel>
              </Button>
            </View>
          </>
        )}
      </ContentBox>
    </ScreenView>
  )
}
